import { useState, useEffect } from "react";
import api, { apiErr } from "@/lib/api";
import { uploadArtFile, artUrl } from "@/lib/uploadArt";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { HelpHint } from "@/components/HelpHint";
import { Upload, Loader2, FileDown, Sparkles, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { confirmAiCost, AI_IMAGE_COST } from "@/lib/aiCost";

const POSITIONS = [
  { id: "front", label: "Front (presenting)" },
  { id: "first_person", label: "First person" },
  { id: "one_hand", label: "One hand" },
  { id: "two_hands", label: "Two hands" },
  { id: "phone_frame", label: "Phone / IG frame" },
];
const STYLES = [
  { id: "diversity", label: "Diverse" },
  { id: "man", label: "Male" },
  { id: "woman", label: "Female" },
  { id: "elegant_nails", label: "Elegant nails" },
  { id: "fancy_black_nails", label: "Fancy black nails" },
];
const CORRECTIONS = [
  { id: "fingers", label: "Fix fingers", text: "Fix ONLY the anatomy: exactly four fingers and one thumb per hand." },
  { id: "arms", label: "Remove arms", text: "Remove any visible arm, wrist, sleeve or body. Only the hand should appear." },
  { id: "contour", label: "Add contour", text: "Add a thick, clean, continuous white wrap contour around the whole hand and product as a single cut piece." },
];

export const ManotaTab = () => {
  const [catalog, setCatalog] = useState([]);
  const [position, setPosition] = useState("front");
  const [handStyle, setHandStyle] = useState("diversity");
  const [productFile, setProductFile] = useState(null);
  const [extra, setExtra] = useState("");
  const [refId, setRefId] = useState("");
  const [trial, setTrial] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [newName, setNewName] = useState("");

  const loadCatalog = () => api.get("/studio/manota-catalog").then((r) => setCatalog(r.data.items || [])).catch(() => {});
  useEffect(() => { loadCatalog(); }, []);

  const addToCatalog = async (f) => {
    if (!f) return;
    try {
      const up = await uploadArtFile(f);
      await api.post("/studio/manota-catalog", { name: newName || f.name, file_id: up.file_id, position, hand_style: handStyle });
      setNewName(""); toast.success("Added to catalog"); loadCatalog();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const delCatalog = async (id) => { try { await api.delete(`/studio/manota-catalog/${id}`); loadCatalog(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };

  const correct = async (correction) => {
    if (!result) return;
    if (!confirmAiCost("This correction")) return;
    setBusy(true);
    try {
      const r = await api.post("/studio/manota-generate", { position, hand_style: handStyle, base_file_id: result.file_id, correction });
      setResult(r.data); toast.success("Correction applied");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const run = async () => {
    setBusy(true); setResult(null);
    try {
      if (trial) {
        if (!productFile) { toast.error("Upload the image you made"); setBusy(false); return; }
        const up = await uploadArtFile(productFile);
        setResult({ file_id: up.file_id, filename: productFile.name });
        toast.success("Uploaded (no credits used)");
      } else {
        if (!confirmAiCost("Generating a manota")) { setBusy(false); return; }
        let productFileId = null;
        if (productFile) { const up = await uploadArtFile(productFile); productFileId = up.file_id; }
        const r = await api.post("/studio/manota-generate", { position, hand_style: handStyle, extra, product_file_id: productFileId, ref_catalog_id: refId || null });
        setResult(r.data); toast.success("Manota generated");
      }
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5 max-w-4xl" data-testid="manota-tab">
      <p className="text-sm text-slate-500 flex items-center gap-2">AI Studio — generate advertising "Manotas".
        <HelpHint side="right">Generate hand mockups holding a client's product. In <b>Trial mode</b> you upload an image you made yourself (no credits). Turn Trial off to generate with AI (uses Emergent credits). Your saved examples act as style references.</HelpHint>
      </p>

      {/* Catalog */}
      <div className="rounded-xl border p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] font-mono uppercase text-slate-500 flex items-center gap-1">Hand catalog
            <HelpHint side="top">Your library of ready manotas. Clients pick a hand style here; it's also used as an AI style reference. Selecting is free — only AI generation costs credits.</HelpHint>
          </span>
          <div className="flex items-center gap-2">
            <Input className="h-8 w-40 text-xs" placeholder="Name" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="manota-cat-name" />
            <label className="inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-xs cursor-pointer hover:border-[#2495D3]" data-testid="manota-cat-add"><Plus size={14} /> Add
              <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => { addToCatalog(e.target.files?.[0]); e.target.value = ""; }} />
            </label>
          </div>
        </div>
        {catalog.length === 0 && <p className="text-xs text-slate-400">No manotas yet. Add your existing designs.</p>}
        <div className="flex flex-wrap gap-2">
          {catalog.map((c) => (
            <button key={c.id} onClick={() => setRefId(refId === c.id ? "" : c.id)} className={`relative rounded-lg border-2 p-0.5 ${refId === c.id ? "border-[#2495D3]" : "border-transparent"}`} data-testid={`manota-cat-${c.id}`} title={c.name}>
              <img src={artUrl(c.file_id)} alt={c.name} className="h-20 w-20 object-cover rounded" />
              <span onClick={(e) => { e.stopPropagation(); delCatalog(c.id); }} className="absolute -top-1 -right-1 bg-white rounded-full text-slate-400 hover:text-red-500"><Trash2 size={13} /></span>
            </button>
          ))}
        </div>
      </div>

      {/* Generate */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div>
            <label className="text-[11px] font-mono uppercase text-slate-500 mb-1 block">Position</label>
            <select className="w-full h-10 rounded-lg border px-2 text-sm" value={position} onChange={(e) => setPosition(e.target.value)} data-testid="manota-position">
              {POSITIONS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-mono uppercase text-slate-500 mb-1 block">Hand style</label>
            <select className="w-full h-10 rounded-lg border px-2 text-sm" value={handStyle} onChange={(e) => setHandStyle(e.target.value)} data-testid="manota-style">
              {STYLES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <label className="flex flex-col items-center justify-center gap-1 h-24 rounded-xl border-2 border-dashed border-slate-300 cursor-pointer hover:border-[#2495D3] text-xs text-slate-500 bg-slate-50/50" data-testid="manota-drop">
            {productFile ? <span className="max-w-[220px] truncate">{productFile.name}</span> : <><Upload size={18} /><span>{trial ? "Upload the manota you made" : "Upload product photo (reference)"}</span></>}
            <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => { setProductFile(e.target.files?.[0]); setResult(null); e.target.value = ""; }} />
          </label>
          {!trial && <Input placeholder="Extra instructions (optional)" value={extra} onChange={(e) => setExtra(e.target.value)} data-testid="manota-extra" />}
          <label className="flex items-center gap-2 text-xs text-slate-600" data-testid="manota-trial-toggle">
            <input type="checkbox" checked={trial} onChange={(e) => setTrial(e.target.checked)} />
            Trial mode — upload instead of generate (no credits)
            <HelpHint side="top">While testing, keep this ON and upload manotas you created in your own ChatGPT account — nothing is charged. Turn OFF to let AI generate (uses credits).</HelpHint>
          </label>
          <Button onClick={run} disabled={busy} data-testid="manota-run">
            {busy ? <><Loader2 size={16} className="animate-spin" /> Working…</> : <><Sparkles size={16} /> {trial ? "Save uploaded manota" : "Generate with AI"}</>}
          </Button>
          {!trial && <p className="text-[11px] text-amber-600">AI generation uses ~{AI_IMAGE_COST.credits} credits (~${AI_IMAGE_COST.usd}) — logged for expense reports.</p>}
        </div>
        <div>
          {result
            ? <div data-testid="manota-result" className="rounded-xl border p-3">
                <img src={artUrl(result.file_id)} alt="manota" className="w-full rounded-lg border mb-3" />
                <a href={artUrl(result.file_id)} target="_blank" rel="noreferrer" download><Button size="sm" data-testid="manota-download"><FileDown size={16} /> Download</Button></a>
                <div className="mt-3 border-t pt-2">
                  <p className="text-[11px] font-mono uppercase text-slate-500 mb-1 flex items-center gap-1">Quick corrections (AI · uses credits)
                    <HelpHint side="top">Re-generate over this result fixing ONE thing while keeping everything else — just like your ChatGPT flow.</HelpHint>
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {CORRECTIONS.map((c) => (<Button key={c.id} size="sm" variant="outline" disabled={busy} onClick={() => correct(c.text)} data-testid={`manota-fix-${c.id}`}>{c.label}</Button>))}
                  </div>
                </div>
              </div>
            : <div className="h-full min-h-[200px] rounded-xl border border-dashed flex items-center justify-center text-sm text-slate-400">Result preview</div>}
        </div>
      </div>
    </div>
  );
};

export default ManotaTab;
